import React, { lazy, Suspense } from "react";
import PropTypes from "prop-types";
import clsx from "clsx";
import Container from "@material-ui/core/Container";
import makeStyles from "@material-ui/styles/makeStyles";
import Typography from "@material-ui/core/Typography";
import useLocals from "@micado-digital/react-ui/hooks/useLocals";
import localDE from "../../../locales/de.json";
import localEN from "../../../locales/en.json";

const Footermenu = lazy(() =>
	import(/* webpackChunkName: "reactFootermenu" */ "./footermenu")
);
const Metamenu = lazy(() => import(/* webpackChunkName: "reactMetamenu" */ "./metamenu"));
const SocialLinks = lazy(() =>
	import(/* webpackChunkName: "reactSocialLinks" */ "./socialLinks")
);
const Micado = lazy(() => import(/* webpackChunkName: "reactMicado" */ "./micado"));
const Logo = lazy(() => import(/* webpackChunkName: "reactLogo" */ "./logo"));

const useStyles = () => {
	return makeStyles(theme => ({
		footer: {
			color: theme?.palette?.secondary?.main,
			paddingBottom: "env(safe-area-inset-bottom)"
		},
		container: {
			display: "grid",
			gridRowGap: theme?.spacing(3),
			padding: theme?.spacing(9.5, 7, 3),
			[theme.breakpoints.up("sm")]: {
				padding: theme?.spacing(11, 7, 5)
			},
			[theme.breakpoints.up("md")]: {
				gridRowGap: theme?.spacing(4),
				padding: theme?.spacing(11, 2, 5)
			},
			[theme.breakpoints.up("lg")]: {
				gridColumnGap: theme?.spacing(4),
				gridRowGap: theme?.spacing(8),
				gridTemplateColumns: "260px 1fr 1fr",
				padding: theme?.spacing(22.5, 7, 5)
			},
			[theme.breakpoints.up("xl")]: {
				gridTemplateColumns: "1fr 1fr 1fr",
				padding: theme?.spacing(25, 17.5, 5)
			}
		},
		headline: {
			gridRow: 1,
			color: "#FFFFFF",
			textAlign: "center",
			[theme.breakpoints.up("lg")]: {
				gridColumn: "1 / 4"
			}
		},
		logoParent: {
			gridRow: 4,
			marginTop: theme?.spacing(3.5),
			[theme.breakpoints.up("lg")]: {
				gridRow: 3,
				gridColumn: "1 / -1",
				marginTop: 0
			},
			"& img": {
				margin: "0 auto"
			}
		},
		customParent: {
			gridRow: 2,
			margin: "0 auto",
			maxWidth: 260,
			[theme.breakpoints.up("md")]: {
				margin: 0,
				maxWidth: "none"
			}
		},
		logoWrapper: {
			alignItems: "center",
			display: "flex",
			flexWrap: "wrap",
			gap: theme?.spacing(2),
			marginTop: theme?.spacing(2)
		},
		logoOeHV: {
			display: "inline-flex",
			marginLeft: -4,
			[theme.breakpoints.down("sm")]: {
				"& img": {
					height: 70,
					width: 70
				}
			}
		},
		logoTirol: {
			display: "inline-flex",
			"& img": {
				filter: "invert(1)"
			},
			[theme.breakpoints.down("sm")]: {
				"& img": {
					height: 69,
					width: 120
				}
			}
		},
		logoUmweltzeichen: {
			display: "inline-flex",
			[theme.breakpoints.down("sm")]: {
				"& img": {
					height: 68,
					width: 70
				}
			}
		},
		logoEcoLabel: {
			display: "inline-flex",
			[theme.breakpoints.down("sm")]: {
				"& img": {
					height: 70,
					width: 70
				}
			}
		},
		logoMichelin: {
			display: "inline-flex",
			[theme.breakpoints.down("sm")]: {
				"& img": {
					height: 70,
					width: 94
				}
			}
		},
		footermenuParent: {
			gridRow: 3,
			margin: "0 auto",
			maxWidth: 260,
			minWidth: 260,
			[theme.breakpoints.up("md")]: {
				margin: 0,
				maxWidth: "none"
			},
			[theme.breakpoints.up("lg")]: {
				gridColumn: "2 / span 2",
				gridRow: 2
			}
		},
		metamenuParent: {
			gridRow: 5,
			[theme.breakpoints.up("lg")]: {
				alignSelf: "center",
				gridColumn: "1 / span 2",
				gridRow: 3
			}
		},
		micadoParent: {
			gridRow: 7,
			marginTop: theme?.spacing(2),
			[theme.breakpoints.up("lg")]: {
				marginBottom: theme?.spacing(6)
			}
		},
		socialLinksParent: {
			display: "flex",
			gridRow: 6,
			justifyContent: "center",
			[theme.breakpoints.up("lg")]: {
				alignSelf: "center",
				gridColumn: 3,
				gridRow: 3,
				justifySelf: "flex-end"
			}
		}
	}))();
};

const ReactFooter02 = props => {
	const {
		copyrights,
		footermenu,
		lang = "de",
		logo,
		logoHeight,
		logoWidth,
		maxWidth = "xl",
		metamenu,
		micado = true,
		socialLinks
	} = props;
	const css = useStyles();
	const l = useLocals({
		lang: lang,
		res: {
			de: localDE,
			en: localEN
		}
	});

	if (!footermenu) return <></>;

	return (
		<div className={clsx("mco-footer-02", css.footer)}>
			<Container
				className={clsx("mco-footer-02__container", css.container)}
				maxWidth={maxWidth}
			>
				<Typography
					className={clsx("mco-footer-headline", css.headline)}
					component="h2"
					variant="h2"
				>
					{l("footer.headlineMountain")}
					<br></br>
					{l("footer.headlineLake")}
				</Typography>
				{logo && (
					<div className={clsx("mco-footer-02__logo-parent", css.logoParent)}>
						<Suspense fallback={<></>}>
							<Logo height={logoHeight} src={logo} width={logoWidth} />
						</Suspense>
					</div>
				)}
				<div className={clsx("mco-footer-02__custom-parent", css.customParent)}>
					{props.children}
					<div className={css.logoWrapper}>
						<a
							className={css.logoOeHV}
							href="https://www.oehv.at/"
							target="_blank"
							rel="noreferrer"
							title="Mitglied - Österreichische Hoteliervereinigung"
						>
							<img
								src="/img/footer/oehv.png"
								srcSet="/img/footer/oehv-hd.png 2x"
								alt="OeHV"
								loading="lazy"
								height={100}
								width={100}
							></img>
						</a>
						<a
							className={css.logoUmweltzeichen}
							href="https://www.ecolabel.eu/"
							target="_blank"
							rel="noreferrer"
							title="Umweltzeichen"
						>
							<img
								src="/img/footer/umweltzeichen.png"
								srcSet="/img/footer/umweltzeichen-hd.png 2x"
								alt="Umweltzeichen"
								loading="lazy"
								height={98}
								width={100}
							></img>
						</a>
						<a
							className={css.logoEcoLabel}
							href="https://www.ecolabel.eu/"
							target="_blank"
							rel="noreferrer"
							title="EU Ecolabel"
						>
							<img
								src="/img/footer/ecolabel-logo.svg"
								alt="EU Ecolabel"
								loading="lazy"
								height={100}
								width={100}
							></img>
						</a>
						<a
							className={css.logoMichelin}
							href="https://guide.michelin.com/"
							target="_blank"
							rel="noreferrer"
							title="Michelin-Empfehlung"
						>
							<img
								src="/img/footer/michelin.svg"
								alt="Michelin-Empfehlung"
								loading="lazy"
								height={78}
								width={104}
							></img>
						</a>
						<a
							className={css.logoTirol}
							href="https://www.convention.tirol/locations/kaiserlodge/"
							target="_blank"
							rel="noreferrer"
							title="Partner of Convention Tirol"
						>
							<img
								src="/img/footer/tirol-convention.png"
								srcSet="/img/footer/tirol-convention-hd.png 2x"
								alt="Partner of Convention Tirol"
								loading="lazy"
								height={92}
								width={160}
							></img>
						</a>
					</div>
				</div>
				{micado && (
					<div className={clsx("mco-footer-02__micado-parent", css.micadoParent)}>
						<Suspense fallback={<></>}>
							<Micado />
						</Suspense>
					</div>
				)}
				{footermenu && (
					<div className={clsx("mco-footer-02__footermenu-parent", css.footermenuParent)}>
						<Suspense fallback={<></>}>
							<Footermenu footermenu={footermenu} />
						</Suspense>
					</div>
				)}
				{metamenu && (
					<div className={clsx("mco-footer-02__metamenu-parent", css.metamenuParent)}>
						<Suspense fallback={<></>}>
							<Metamenu copyrights={copyrights} metamenu={metamenu} />
						</Suspense>
					</div>
				)}
				{socialLinks && (
					<div className={clsx("mco-footer-02__social-links-parent", css.socialLinksParent)}>
						<Suspense fallback={<></>}>
							<SocialLinks socialLinks={socialLinks} />
						</Suspense>
					</div>
				)}
			</Container>
		</div>
	);
};

ReactFooter02.propTypes = {
	copyrights: PropTypes.string,
	footermenu: PropTypes.object.isRequired,
	logo: PropTypes.string,
	logoAlternative: PropTypes.object,
	logoHeight: PropTypes.number,
	logoWidth: PropTypes.number,
	maxWidth: PropTypes.string,
	metamenu: PropTypes.object,
	micado: PropTypes.bool,
	socialIconSize: PropTypes.number,
	socialLinks: PropTypes.array
};

export default ReactFooter02;
